import React, { createContext, useContext, useMemo, useState } from 'react';

type SlideMenuContent = {
  header?: React.ReactNode;
  content: React.ReactNode;
} | null;

interface SlideMenuContextType {
  slideMenuContent: SlideMenuContent;
  isSlideMenuOpen: boolean;
  openSlideMenu: (content: SlideMenuContent) => void;
  closeSlideMenu: () => void;
  containerHeight: string | number;
  setContainerHeight: (height: string | number) => void;
}

interface SlideMenuProviderProps {
  children: React.ReactNode;
}

const SlideMenuContext = createContext<SlideMenuContextType | undefined>(undefined);

export const useSlideMenuContext = (): SlideMenuContextType => {
  const context = useContext(SlideMenuContext);

  if (!context) {
    throw new Error('useSlideMenuContext must be used within a SlideMenuContextProvider');
  }

  return context;
};

export const SlideMenuProvider: React.FC<SlideMenuProviderProps> = ({ children }) => {
  const [slideMenuContent, setSlideMenuContent] = useState<SlideMenuContent>(null);
  const [isSlideMenuOpen, setIsSlideMenuOpen] = useState(false);
  const [containerHeight, setContainerHeight] = useState<string | number>('initial');

  const openSlideMenu = (content: SlideMenuContent) => {
    setSlideMenuContent(content);
    setIsSlideMenuOpen(true);
  };

  const closeSlideMenu = () => {
    setIsSlideMenuOpen(false);
  };

  const contextValue = useMemo(
    () => ({
      slideMenuContent,
      isSlideMenuOpen,
      openSlideMenu,
      closeSlideMenu,
      containerHeight,
      setContainerHeight,
    }),
    [slideMenuContent, isSlideMenuOpen, containerHeight],
  );

  return <SlideMenuContext.Provider value={contextValue}>{children}</SlideMenuContext.Provider>;
};
